import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SecureMacPolicyLayout, { SecureMacPolicySection } from "@/components/SecureMacPolicyLayout";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiError, publicApi } from "@/lib/api";

export default function SecureMacAccountDeletion() {
  const { customer, ready } = useCustomerAuth();
  const [email, setEmail] = useState("");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState("");

  useEffect(() => {
    if (customer?.email) {
      setEmail((current) => current || customer.email);
    }
  }, [customer]);

  const requestDeletion = async (event) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await publicApi.post("/halo/account-deletion", {
        email,
        billing_source: "stripe",
        privacy_consent: privacyConsent,
        website,
      });
      setRequestId(String(data?.id || "received"));
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SecureMacPolicyLayout
      eyebrow="LightningFlow account"
      title="Request deletion of your unified LightningFlow account."
      description="Request verified deletion of the LightningFlow identity shared by SecureShield Pro and your other LightningFlow products. Billing subscriptions are managed separately by their payment provider."
      seoTitle="Delete a SecureShield Account — LightningFlow"
      canonicalPath="/secureshield/account-deletion"
      noIndex
    >
      <SecureMacPolicySection title="Before requesting deletion">
        <p>
          Once ownership is verified, deletion signs out product sessions, revokes SecureShield Pro
          device activations and removes account information that is not required for billing
          disputes, fraud prevention, tax or other legal obligations.
        </p>
        <p>
          Deletion does not automatically cancel subscriptions billed by Stripe, Apple, Google or
          Microsoft. Cancel recurring billing with the provider that manages the purchase.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Start a verified request">
        {requestId ? (
          <div className="border-l-2 border-electric bg-electric-soft p-5" role="status">
            <h3 className="font-display text-xl font-bold">Request received</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-600">
              We will contact the account email to verify ownership before deleting any data.
              Reference: <span className="font-mono">{requestId}</span>
            </p>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={requestDeletion}>
            <p className="text-sm leading-relaxed text-ink-600">
              {ready && customer
                ? `Signed in as ${customer.email}. Confirm the account email below.`
                : "Enter the email used for your LightningFlow account. You do not need to provide a password, card number or store login."}
            </p>
            <label className="block">
              <span className="lf-label">LightningFlow account email</span>
              <input
                className="lf-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <input
              className="hidden"
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
            <label className="flex gap-3 text-sm leading-relaxed text-ink-600">
              <input
                className="mt-1 h-4 w-4 accent-electric"
                type="checkbox"
                required
                checked={privacyConsent}
                onChange={(event) => setPrivacyConsent(event.target.checked)}
              />
              <span>
                I agree to the <Link className="underline" to="/privacy">privacy handling</Link> needed
                to verify and complete this request.
              </span>
            </label>
            {error ? <p className="text-sm text-red-700">{error}</p> : null}
            <button className="lf-btn-primary" type="submit" disabled={busy || !privacyConsent}>
              {busy ? "Submitting…" : "Request account deletion"}
            </button>
          </form>
        )}
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Company">
        <p>TripleThree Global Pty Ltd trading as LightningFlow · South Australia · contact@lightningflow.ai</p>
      </SecureMacPolicySection>
    </SecureMacPolicyLayout>
  );
}
